import React, { useState, useEffect } from "react";
import { colorOf } from "@/lib/constants";
import { moodEmoji, sleepEmoji } from "@/lib/vibe";
import { api } from "@/lib/api";
import { useSuspendPollingWhileOpen } from "@/lib/dialogPolling";
import { toast } from "sonner";
import { X } from "lucide-react";

// Simplified check-in for young kids (< 7). Only mood + sleep, huge sliders, big emoji faces.
// When opened by a parent/grandparent/other (not a kid), we also show a small
// "Parent note" field — perfect for little reminders like "Getting picked up by granny".
export default function KidCheckinDialog({ member, existing, viewer, onClose, onSaved }) {
  useSuspendPollingWhileOpen();
  const [mood, setMood] = useState(existing?.mood ?? 7);
  const [sleep, setSleep] = useState(existing?.sleep ?? 7);
  const [note, setNote] = useState(existing?.note || "");
  const [saving, setSaving] = useState(false);

  // Parent-ish viewer = anyone who isn't a kid themselves (or no viewer set yet → assume parent).
  const canAddNote = !viewer || viewer.role !== "kid";

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const color = colorOf(member.color);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await api.upsertCheckin({
        member_id: member.id,
        sleep: Number(sleep),
        mood: Number(mood),
        tags: existing?.tags || [],
        status: existing?.status || "home",
        note: canAddNote ? (note.trim() || null) : (existing?.note || null),
        custom_location: existing?.custom_location || null,
        note_private: !!existing?.note_private,
        today_plan: existing?.today_plan || null,
      });
      toast.success("Yay! Saved 🌟");
      onSaved(updated);
    } catch (e) {
      toast.error("Oops — try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6"
      style={{ background: "rgba(20,20,18,0.55)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="kid-checkin-dialog"
    >
      <div
        className="bg-[color:var(--surface)] rounded-xl w-full max-w-lg overflow-hidden border border-[color:var(--line)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="px-6 pt-6 pb-5 flex items-center gap-4"
          style={{ background: color.hex }}
        >
          <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center text-5xl flex-shrink-0 shadow-sm">
            {member.avatar_emoji}
          </div>
          <div className="flex-1">
            <div className="label-tiny opacity-70">Hi</div>
            <h2 className="font-display text-4xl leading-tight">
              {member.name}!
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white/70 flex items-center justify-center hover:bg-white transition-colors"
            data-testid="kid-checkin-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>

        <div className="px-6 py-8 space-y-10">
          {/* Mood */}
          <div>
            <div className="text-center mb-5">
              <div className="font-display-italic text-3xl mb-1">
                How do you feel?
              </div>
              <div className="text-[96px] leading-none" data-testid="kid-mood-emoji">
                {moodEmoji(Number(mood))}
              </div>
            </div>
            <input
              type="range"
              min="1"
              max="10"
              value={mood}
              onChange={(e) => setMood(e.target.value)}
              className="ff-slider-xl"
              data-testid="kid-mood-slider"
              aria-label="mood"
            />
            <div className="flex justify-between label-tiny mt-2 px-1">
              <span>Not great</span>
              <span>Amazing</span>
            </div>
          </div>

          {/* Sleep */}
          <div>
            <div className="text-center mb-5">
              <div className="font-display-italic text-3xl mb-1">
                How did you sleep?
              </div>
              <div className="text-[96px] leading-none" data-testid="kid-sleep-emoji">
                {sleepEmoji(Number(sleep))}
              </div>
            </div>
            <input
              type="range"
              min="1"
              max="10"
              value={sleep}
              onChange={(e) => setSleep(e.target.value)}
              className="ff-slider-xl"
              data-testid="kid-sleep-slider"
              aria-label="sleep"
            />
            <div className="flex justify-between label-tiny mt-2 px-1">
              <span>Barely</span>
              <span>Super well</span>
            </div>
          </div>

          {/* Parent note — only shown to parent/grandparent/other viewers */}
          {canAddNote && (
            <div>
              <label className="label-tiny block mb-2">
                Parent note for {member.name} (optional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={500}
                rows={2}
                placeholder="e.g. Getting picked up by granny"
                className="ff-input resize-none"
                data-testid="kid-parent-note"
              />
            </div>
          )}
        </div>

        <div className="px-6 pb-6">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full font-display text-2xl bg-[color:var(--ink)] text-[color:var(--bg)] rounded-xl py-5 hover:bg-[#2a2a26] transition-colors disabled:opacity-60"
            data-testid="kid-checkin-save"
          >
            {saving ? "Saving…" : "All done! ✨"}
          </button>
        </div>
      </div>
    </div>
  );
}
